import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import BeefreeSDK from "@beefree.io/sdk";
import { ExecCommands } from "@beefree.io/sdk/dist/types/bee";
import type { BeePluginError, IBeeConfig, IToken } from "@beefree.io/sdk/dist/types/bee";
import campaignService from "../services/campaignService";
import { selectUserId } from "../store/slices/authUserSlice";

const fetchBeeFreeToken = async (): Promise<IToken> => {
  const token = await campaignService.getBeeFreeToken();
  return {
    access_token: token,
    token_type: "bearer",
    expires_in: 300,
  } as unknown as IToken;
};

export interface BeeEditorHandle {
  /** Save the editor; resolves with the latest design JSON + rendered HTML. */
  save: () => Promise<{ design: object; html: string } | undefined>;
  /** Scroll to and highlight the row at the given 0-based index. */
  highlightRow: (rowIndex: number) => Promise<void>;
}

export interface BeeEditorProps {
  /** Full BeeFree page design to load into the editor. */
  design: object;
  /** Unique DOM container id for this editor instance. */
  containerId: string;
  /** Fires as the user edits; provides the current design JSON. */
  onChange?: (design: object) => void;
  /** Fires on save; provides the design JSON and rendered HTML. */
  onSave?: (design: object, html: string) => void;
  /** Fires once the editor has finished loading and is ready to use. */
  onReady?: () => void;
  height?: string | number;
}

/**
 * Thin, reusable wrapper around the BeeFree SDK. Initializes once per mount —
 * to load a different design, remount with a new React `key`.
 *
 * Extracted from the inline integration in NewsletterEdition.tsx.
 */
const BeeEditor = forwardRef<BeeEditorHandle, BeeEditorProps>(
  ({ design, containerId, onChange, onSave, onReady, height = "100%" }, ref) => {
    const userId = useSelector(selectUserId);
    const beeRef = useRef<BeefreeSDK | null>(null);
    const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const initializedRef = useRef(false);

    useImperativeHandle(ref, () => ({
      save: async () => {
        const res = await beeRef.current?.save();
        if (!res) return undefined;
        return {
          design: JSON.parse(res.data.json),
          html: res.data.html,
        };
      },
      highlightRow: async (rowIndex: number) => {
        const bee = beeRef.current;
        if (!bee) return;
        // BeeFree `execCommand` `target.row` is 1-based (row 1 = first row),
        // but callers pass a 0-based index into page.rows. Convert here,
        // matching the `rowIndex + 1` convention in NewsletterEdition.tsx.
        const beeRow = rowIndex + 1;
        const scroll = await bee.execCommand(ExecCommands.SCROLL, {
          target: { row: beeRow },
        });
        const highlight = await bee.execCommand(ExecCommands.HIGHLIGHT, {
          target: { row: beeRow },
        });
        if (scroll?.status === "error" || highlight?.status === "error") {
          console.warn("BeeEditor highlightRow failed", rowIndex, scroll, highlight);
        }
      },
    }));

    useEffect(() => {
      if (initializedRef.current) return;
      initializedRef.current = true;

      const config: IBeeConfig = {
        uid: userId || "content-tools-operator",
        container: containerId,
        language: "en-US",
        trackChanges: true,
        onChange: (jsonString: string) => {
          try {
            onChange?.(JSON.parse(jsonString));
          } catch (e) {
            console.error("BeeEditor onChange parse error", e);
          }
        },
        onSave: (jsonString: string, html: string) => {
          try {
            onSave?.(JSON.parse(jsonString), html);
          } catch (e) {
            console.error("BeeEditor onSave parse error", e);
          }
        },
        onError: (error: BeePluginError) => {
          console.error("BeeFree editor error", error);
        },
      };

      fetchBeeFreeToken()
        .then((token) => {
          beeRef.current = new BeefreeSDK(token);
          return beeRef.current.start(config, design);
        })
        .then(() => {
          setStatus("ready");
          onReady?.();
        })
        .catch((error) => {
          console.error("Error configuring BeeFree editor", error);
          setErrorMessage(
            error?.message || "Failed to load the editor. Please try again.",
          );
          setStatus("error");
        });
      // Init is intentionally once-per-mount; design is captured on first run.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <div style={{ position: "relative", width: "100%", height }}>
        {status === "loading" && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#718096",
              fontSize: "14px",
              zIndex: 1,
            }}
          >
            Loading editor…
          </div>
        )}
        {status === "error" && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#C53030",
              fontSize: "14px",
              padding: "16px",
              textAlign: "center",
              zIndex: 1,
            }}
          >
            {errorMessage}
          </div>
        )}
        <div id={containerId} style={{ width: "100%", height: "100%" }} />
      </div>
    );
  },
);

BeeEditor.displayName = "BeeEditor";

export default BeeEditor;
